import React, { FC, useEffect, useRef, useState } from 'react';

import { DateTime } from 'luxon';
import { FormattedMessage } from 'react-intl';
import { useParams } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';

import { BaseStyle } from '@nhl/nhl-breakaway-ui';
import LoadingDisplay from '@nhl/nhl-react-ui/lib/LoadingDisplay';

import { NhlEdgeIcon } from '~/core/customIcons';
import { getDataLayer } from '~/core/googleAnalyticsUtils';
import { useSizeWithThrottle } from '~/core/hooks/useSizeWithThrottle';
import { parseValueFromLocalizedAttribute } from '~/core/utils';
import PPTReplay from '~/views/pages/gameCenter/components/pptReplay/PPTReplay';
import GoalScorer from '~/views/pages/gameCenter/components/scoringSummary/components/goalScorer/GoalScorer';

import { usePptReplayQuery } from './api';
import { PptReplay } from './types';

import { MatchupDate, PptReplayEmbedContainer, PptReplayHeader, TeamMatchUp } from './styles';

const PptReplayEmbed: FC = () => {
    const { gameId, eventNumber } = useParams();
    const size = useSizeWithThrottle();

    const { data: pptEmbedData, isLoading, hasLoaded } = usePptReplayQuery(gameId, eventNumber);

    const [hasSentOpenEvent, setHasSentOpenEvent] = useState<boolean>(false);
    const [hasSentInitialResizeEvent, setHasSentInitialResizeEvent] = useState<boolean>(false);
    const embedContainerRef = useRef<HTMLDivElement | null>(null);

    const { homeTeam, awayTeam, goal, gameDate, gameState, gameType, id }: PptReplay = pptEmbedData;
    const date = DateTime.fromISO(gameDate).toLocaleString({ month: 'long', day: 'numeric', year: 'numeric' });

    const sendEmbedHeightToParent = () => {
        // this checks to make sure we're actually in an embedded environment
        if (window.self !== window.top && embedContainerRef.current !== null) {
            // + 2 accounts for the border height
            parent.postMessage({ height: embedContainerRef.current.clientHeight + 2 }, '*');
        }
    };

    useEffect(() => {
        if (gameId && eventNumber && !hasSentOpenEvent) {
            getDataLayer().push({
                event: 'goal_visualizer_embed_open',
                game_id: gameId,
                event_id: eventNumber,
                // TODO: Hide parent_url for now until we get approval from legal
                // window.parent.location.href can't be accessed on a cross-origin frame, but we can use
                // document.referrer, which returns the URI of the page that linked to this page
                // parent_url: window.location != window.parent.location ? document.referrer : document.location.href,
            });
            setHasSentOpenEvent(true);
        }
    }, [gameId, eventNumber, hasSentOpenEvent]);

    // the first useEffect here will send the embed height to the parent when the ppt replay has finished
    // its initial load. The second will send the height whenever the size of the page changes.
    useEffect(() => {
        if (hasLoaded && !hasSentInitialResizeEvent) {
            sendEmbedHeightToParent();
            setHasSentInitialResizeEvent(true);
        }
    }, [hasLoaded, hasSentInitialResizeEvent, size]);

    useEffect(() => {
        sendEmbedHeightToParent();
    }, [size]);

    return (
        <ThemeProvider theme={BaseStyle.LightTheme}>
            <BaseStyle.RootTheming>
                {isLoading ? (
                    <LoadingDisplay isLoading />
                ) : (
                    <PptReplayEmbedContainer ref={embedContainerRef}>
                        <PptReplayHeader>
                            <NhlEdgeIcon />
                            <FormattedMessage
                                defaultMessage="Goal Visualizer"
                                description="A header describing a Goal Visualizer component"
                            />
                        </PptReplayHeader>
                        <div>
                            <TeamMatchUp>
                                <div>
                                    {parseValueFromLocalizedAttribute(awayTeam.placeName)}&nbsp;
                                    {parseValueFromLocalizedAttribute(awayTeam.name)} @&nbsp;
                                    {parseValueFromLocalizedAttribute(homeTeam.placeName)}&nbsp;
                                    {parseValueFromLocalizedAttribute(homeTeam.name)}
                                </div>
                                <MatchupDate>{date}</MatchupDate>
                            </TeamMatchUp>
                            <PPTReplay
                                gameId={id}
                                gameState={gameState}
                                homeTeam={homeTeam}
                                awayTeam={awayTeam}
                                homeTeamDefendingSide={goal.homeTeamDefendingSide}
                                goalScorerId={goal.playerId}
                                pptReplayUrl={goal.pptReplayUrl}
                                showGameCenterButton
                                isEmbed
                                eventId={goal.eventId}
                            />
                        </div>
                        <GoalScorer
                            gameId={id}
                            homeTeam={homeTeam}
                            awayTeam={awayTeam}
                            gameState={gameState}
                            goal={goal}
                            logoUrl={goal.logoUrl}
                            isLarge
                            isLimitedScoring={false}
                            isWebview
                            isPptEmbed
                            gameType={gameType}
                        />
                    </PptReplayEmbedContainer>
                )}
            </BaseStyle.RootTheming>
        </ThemeProvider>
    );
};

export { PptReplayEmbed as Component };
